import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui";
import { Icon } from "./Icon";
import { apiGet, apiPost } from "../lib/api";
import type { Phase1JobPoll } from "../lib/phase1Job";
import { formatApiError } from "../lib/utils";

export type TargetRunRow = {
  phase1_job_id: string;
  run_id: string;
  created_at: string;
  target_display_name?: string;
  main_contractor?: string;
  bucket_id?: string;
  evidence_rows: number;
  status: string;
  phase?: string;
  job_type?: string;
  is_canonical?: boolean;
  can_stop?: boolean;
  error?: string;
};

type TargetRunsResponse = {
  ok?: boolean;
  target_display_name?: string;
  canonical_job_id?: string;
  runs: TargetRunRow[];
};

export type DeskJobsPanelMode = "phase1" | "extraction" | "synthesis";

function statusClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "running" || s === "pending") return "text-primary";
  if (s === "ok" || s === "completed") return "text-secondary";
  if (s === "cancelled" || s === "interrupted") return "text-on-surface-variant";
  if (s === "error") return "text-error";
  return "text-on-surface-variant";
}

function formatWhen(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16);
  }
}

type Props = {
  id?: string;
  mode?: DeskJobsPanelMode;
  targetName?: string;
  /** Phase 2 evidence job id (canonical) — used when mode is extraction. */
  jobId?: string;
  onOpenRun?: (runId: string) => void;
  /** Called when the list finds a pending/running job (e.g. resume polling). */
  onActiveRun?: (runId: string) => void;
  className?: string;
  embedded?: boolean;
  highlightRunId?: string;
  liveProgress?: Phase1JobPoll["progress"] | null;
  defaultCollapsed?: boolean;
  /** Increment to force reload (e.g. after starting a new run). */
  refreshToken?: number;
  /** Hide expand/collapse row (e.g. inside a drawer with its own header). */
  showToggle?: boolean;
  /** Fill available height instead of capped max-height scroll area. */
  fillHeight?: boolean;
};

export function TargetJobRunsPanel({
  id,
  mode = "phase1",
  targetName = "",
  jobId = "",
  onOpenRun,
  onActiveRun,
  className = "",
  embedded = false,
  highlightRunId = "",
  liveProgress = null,
  defaultCollapsed,
  refreshToken = 0,
  showToggle = true,
  fillHeight = false,
}: Props) {
  const isExtraction = mode === "extraction";
  const isSynthesis = mode === "synthesis";
  const [collapsed, setCollapsed] = useState(
    defaultCollapsed !== undefined ? defaultCollapsed : embedded,
  );
  const [data, setData] = useState<TargetRunsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [stopping, setStopping] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (isSynthesis) {
      setLoading(true);
      try {
        const q = targetName.trim()
          ? `?target=${encodeURIComponent(targetName.trim())}`
          : "";
        const res = await apiGet<TargetRunsResponse>(`/api/phase3/synthesis-runs${q}`);
        setData(res);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (isExtraction) {
      const jid = jobId.trim();
      if (!jid) {
        setData(null);
        return;
      }
      setLoading(true);
      try {
        const res = await apiGet<TargetRunsResponse>(
          `/api/phase2/extraction-runs?job_id=${encodeURIComponent(jid)}`,
        );
        setData(res);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
      return;
    }
    const name = targetName.trim();
    if (!name) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const res = await apiGet<TargetRunsResponse>(
        `/api/phase1/target-runs?target=${encodeURIComponent(name)}`,
      );
      const phase1Only = {
        ...res,
        runs: (res.runs || []).filter(
          (r) => (r.job_type || "phase1_execute") === "phase1_execute",
        ),
      };
      setData(phase1Only);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isExtraction, isSynthesis, jobId, targetName]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const runs = data?.runs || [];
  const hasActive = runs.some((r) =>
    ["pending", "running"].includes(String(r.status || "").toLowerCase()),
  );

  useEffect(() => {
    if (hasActive && embedded) {
      setCollapsed(false);
    }
  }, [hasActive, embedded]);

  useEffect(() => {
    if (!hasActive || !onActiveRun) return;
    const active = runs.find((r) =>
      ["pending", "running"].includes(String(r.status || "").toLowerCase()),
    );
    if (active?.run_id) onActiveRun(active.run_id);
  }, [hasActive, runs, onActiveRun]);

  useEffect(() => {
    const ready = isExtraction ? jobId.trim() : isSynthesis ? true : targetName.trim();
    if (!hasActive || !ready) return;
    const iv = window.setInterval(load, 12000);
    return () => window.clearInterval(iv);
  }, [hasActive, isExtraction, isSynthesis, jobId, targetName, load]);

  const stopRun = async (runId: string) => {
    if (!runId || stopping) return;
    setStopping(runId);
    try {
      await apiPost(`/api/jobs/${encodeURIComponent(runId)}/cancel`, {});
      await load();
    } catch (e) {
      window.alert(formatApiError(e));
    } finally {
      setStopping(null);
    }
  };

  const displayTarget = data?.target_display_name || targetName || "—";
  const titlePrefix = isSynthesis
    ? "PDR synthesis jobs"
    : isExtraction
      ? "Extraction jobs"
      : "Phase 1 jobs";
  const countLabel = isSynthesis ? "Projects" : isExtraction ? "Processed" : "Pages";
  const modelColumnLabel = isSynthesis ? "Contractor" : "Model";
  const emptyMessage = isSynthesis
    ? "No PDR synthesis runs yet."
    : isExtraction
      ? "No contractor extraction runs yet for this job."
      : "No Phase 1 runs yet for this target.";

  const missingContext = isExtraction ? !jobId.trim() : isSynthesis ? false : !targetName.trim();
  if (missingContext) {
    const emptyClass = embedded
      ? `border-t border-outline-variant/60 shrink-0 px-4 py-3 text-xs text-on-surface-variant ${className}`
      : `px-6 py-2 border-b border-outline-variant bg-surface-container-lowest text-xs text-on-surface-variant ${className}`;
    return (
      <section className={emptyClass}>
        {isExtraction
          ? "Select a target job to see extraction run history."
          : "Set a target company to see Phase 1 job history."}
      </section>
    );
  }

  const activeCount = runs.filter((r) =>
    ["pending", "running"].includes(String(r.status || "").toLowerCase()),
  ).length;
  const progressQueued = Number(liveProgress?.queries_queued ?? 0);
  const progressDone = Number(liveProgress?.queries_done ?? 0);
  const progressLabel =
    progressQueued > 0 && highlightRunId && liveProgress
      ? isSynthesis
        ? ` · PDR ${progressDone}/${progressQueued}`
        : ` · extracting ${progressDone}/${progressQueued}`
      : "";
  const summaryMeta = [
    `${runs.length} job${runs.length === 1 ? "" : "s"}`,
    !isExtraction && data?.canonical_job_id
      ? `canonical ${data.canonical_job_id.slice(0, 8)}…`
      : "",
    activeCount > 0 ? `${activeCount} active` : "",
    progressLabel,
    collapsed ? "click to expand" : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const sectionClass = fillHeight
    ? `flex flex-col flex-1 min-h-0 ${className}`
    : embedded
      ? `border-t border-outline-variant/60 shrink-0 ${className}`
      : `border-b border-outline-variant bg-surface-container-low shrink-0 ${className}`;

  const padX = fillHeight ? "px-5" : embedded ? "px-4" : "px-6";
  const tableExpanded = showToggle ? !collapsed : true;

  return (
    <section id={id} className={sectionClass}>
      {showToggle ? (
        <div className={`${padX} py-2 flex items-center justify-between gap-3 shrink-0`}>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="flex-1 min-w-0 flex items-center gap-2 text-left hover:opacity-90"
            aria-expanded={!collapsed}
          >
            <Icon
              name={collapsed ? "expand_more" : "expand_less"}
              className="text-on-surface-variant shrink-0 text-lg"
            />
            <div className="min-w-0">
              <p className="font-data-label text-data-label uppercase text-on-surface-variant">
                {titlePrefix} · {displayTarget}
              </p>
              <p className="text-body-sm font-data-mono text-on-surface-variant mt-0.5 truncate">
                {loading && !runs.length ? "Loading…" : summaryMeta}
              </p>
            </div>
          </button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              load();
            }}
            disabled={loading}
            variant="link-muted"
            className="shrink-0"
          >
            Refresh
          </Button>
        </div>
      ) : (
        <div
          className={`${padX} py-3 flex items-center justify-between gap-3 shrink-0 border-b border-outline-variant/50`}
        >
          <p className="text-body-sm font-data-mono text-on-surface-variant truncate min-w-0">
            {loading && !runs.length ? "Loading…" : summaryMeta}
          </p>
          <Button onClick={() => load()} disabled={loading} variant="link-muted" className="shrink-0">
            Refresh
          </Button>
        </div>
      )}
      {tableExpanded && liveProgress && highlightRunId ? (
        <div className={`${padX} py-2 border-t border-outline-variant/40 bg-primary/5 shrink-0`}>
          <p className="text-[10px] uppercase tracking-widest text-primary mb-1">
            Live · {progressDone} / {progressQueued} {isSynthesis ? "steps" : "pages"}
          </p>
          <p className="text-[11px] font-data-mono text-on-surface-variant truncate">
            {liveProgress.current_title || liveProgress.current_url || "Processing…"}
          </p>
        </div>
      ) : null}
      {tableExpanded ? (
        <div
          className={`overflow-auto custom-scrollbar flex-1 min-h-0 ${
            fillHeight ? "" : embedded ? "max-h-[220px]" : "max-h-[140px]"
          }`}
        >
          <table className="w-full text-left text-[11px] font-data-mono">
            <thead className="text-on-surface-variant uppercase text-[10px] border-t border-outline-variant/60">
              <tr>
                <th className="px-4 py-1.5 font-normal">When</th>
                <th className="px-2 py-1.5 font-normal">Status</th>
                <th className="px-2 py-1.5 font-normal">{modelColumnLabel}</th>
                <th className="px-2 py-1.5 font-normal">{countLabel}</th>
                <th className="px-2 py-1.5 font-normal">Run</th>
                <th className="px-4 py-1.5 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-on-surface-variant">
                    {loading ? "Loading…" : emptyMessage}
                  </td>
                </tr>
              ) : (
                runs.map((row) => {
                  const runId = row.run_id || "";
                  return (
                    <tr
                      key={`${row.phase1_job_id}-${row.run_id}-${row.job_type}`}
                      className={`border-t border-outline-variant/40 hover:bg-surface-variant/30 ${
                        highlightRunId && runId === highlightRunId ? "bg-primary/10" : ""
                      }`}
                    >
                      <td className="px-4 py-2 whitespace-nowrap text-on-surface-variant">
                        {formatWhen(row.created_at)}
                        {row.is_canonical && !isExtraction ? (
                          <span className="ml-1 text-primary text-[9px] uppercase">
                            canonical
                          </span>
                        ) : null}
                      </td>
                      <td className={`px-2 py-2 capitalize ${statusClass(row.status)}`}>
                        {row.status || "—"}
                      </td>
                      <td className="px-2 py-2 text-on-surface-variant truncate max-w-[8rem]">
                        {isSynthesis
                          ? row.main_contractor || row.phase || "—"
                          : row.phase || row.job_type || "—"}
                      </td>
                      <td className="px-2 py-2 text-on-surface">{row.evidence_rows}</td>
                      <td className="px-2 py-2 text-on-surface-variant truncate max-w-[7rem]">
                        {runId ? `${runId.slice(0, 14)}…` : "—"}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {runId ? (
                          <Link
                            to={`/logs?run=${encodeURIComponent(runId)}`}
                            className="text-primary hover:underline mr-3"
                          >
                            Log
                          </Link>
                        ) : null}
                        {runId && onOpenRun ? (
                          <button
                            type="button"
                            onClick={() => onOpenRun(runId)}
                            className="text-primary hover:underline mr-3"
                          >
                            {isExtraction || isSynthesis ? "Track" : "Progress"}
                          </button>
                        ) : null}
                        {row.can_stop && runId ? (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => stopRun(runId)}
                            disabled={stopping === runId}
                            className="px-2 py-0.5 text-data-label"
                          >
                            {stopping === runId ? "Stopping…" : "Stop"}
                          </Button>
                        ) : (
                          <span className="text-outline text-[10px]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
